/**
 * TypeFilter Component
 * Multi-select filter for OKR types
 */

import React from 'react';
import { Building2, Users, User, Briefcase } from 'lucide-react';

interface TypeFilterProps {
  availableTypes: string[];
  selectedTypes: string[];
  onChange: (types: string[]) => void;
}

const TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  company: { label: 'Company', icon: Building2, color: 'bg-primary-100 text-primary-700 border-primary-300' },
  department: { label: 'Department', icon: Briefcase, color: 'bg-success-100 text-success-700 border-success-300' },
  team: { label: 'Team', icon: Users, color: 'bg-purple-100 text-purple-700 border-purple-300' },
  individual: { label: 'Individual', icon: User, color: 'bg-warning-100 text-warning-700 border-warning-300' },
};

export const TypeFilter: React.FC<TypeFilterProps> = ({
  availableTypes,
  selectedTypes,
  onChange,
}) => {
  const handleToggle = (type: string) => {
    if (selectedTypes.includes(type)) {
      onChange(selectedTypes.filter((t) => t !== type));
    } else {
      onChange([...selectedTypes, type]);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-stone-700">
        OKR Type
      </label>
      <div className="space-y-2">
        {availableTypes.map((type) => {
          const config = TYPE_CONFIG[type] || { label: type, icon: Building2, color: 'bg-stone-100 text-stone-700 border-stone-300' };
          const Icon = config.icon;
          const isSelected = selectedTypes.includes(type);

          return (
            <button
              key={type}
              onClick={() => handleToggle(type)}
              className={`w-full flex items-center gap-2 px-3 py-2 border-2 rounded-lg transition-all ${
                isSelected
                  ? `${config.color} border-opacity-100 shadow-sm`
                  : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium flex-1 text-left">{config.label}</span>
              {isSelected && (
                <div className="w-4 h-4 rounded-full bg-current opacity-20" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
